import { useEffect, useState } from "react"
import axios from "axios"
import {
  getPermitWorkflowTimeline,
  type PermitWorkflowTimeline,
} from "modules/permit"

interface UsePermitWorkflowTimelineParams {
  tenantId: string
  permitId?: string | null
  enabled?: boolean
  reloadToken?: number
}

interface UsePermitWorkflowTimelineResult {
  workflowTimeline: PermitWorkflowTimeline | null
  isWorkflowTimelineLoading: boolean
  workflowTimelineError: string | null
}

export const usePermitWorkflowTimeline = ({
  tenantId,
  permitId,
  enabled = false,
  reloadToken = 0,
}: UsePermitWorkflowTimelineParams): UsePermitWorkflowTimelineResult => {
  const [workflowTimeline, setWorkflowTimeline] = useState<PermitWorkflowTimeline | null>(null)
  const [isWorkflowTimelineLoading, setIsWorkflowTimelineLoading] = useState(false)
  const [workflowTimelineError, setWorkflowTimelineError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled || !permitId) {
      setWorkflowTimeline(null)
      setWorkflowTimelineError(null)
      setIsWorkflowTimelineLoading(false)
      return
    }

    let cancelled = false
    setIsWorkflowTimelineLoading(true)
    setWorkflowTimelineError(null)

    getPermitWorkflowTimeline(tenantId, permitId)
      .then((timeline) => {
        if (cancelled) return
        setWorkflowTimeline(timeline)
      })
      .catch((error) => {
        if (cancelled) return
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          setWorkflowTimeline(null)
          setWorkflowTimelineError(null)
          return
        }
        console.error("Failed to load workflow timeline", error)
        const message =
          (error.response?.data as any)?.message ??
          error.response?.statusText ??
          error.message ??
          "ไม่สามารถโหลดข้อมูล Workflow ได้"
        setWorkflowTimelineError(message)
        setWorkflowTimeline(null)
      })
      .finally(() => {
        if (!cancelled) {
          setIsWorkflowTimelineLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [tenantId, permitId, enabled, reloadToken])

  return {
    workflowTimeline,
    isWorkflowTimelineLoading,
    workflowTimelineError,
  }
}
